import { useLocation, useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, Mail, ArrowRight, Sparkles, Calendar,
  User, Building2, CreditCard
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const CRMSubscriptionSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, company, plan, trialDays } = location.state || {
    email: 'your email',
    company: 'Your Company',
    plan: 'Growth',
    trialDays: 14
  };

  // Calculate trial end date
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + (trialDays || 14));
  const formattedDate = trialEndDate.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <Header />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="mb-8 relative inline-block">
            <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl animate-pulse"></div>
            <div className="relative w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Success Message */}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Welcome to CLOUD IT CRM!
          </h1>
          <p className="text-slate-400 text-lg mb-8">
            Your {trialDays}-day free trial has started. Check your email for login credentials.
          </p>

          {/* Details Card */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-8 text-left">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-400" />
              Your Account Details
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl">
                <Building2 className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Company</p>
                  <p className="text-white font-medium">{company}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Login Email</p>
                  <p className="text-white font-medium">{email}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl">
                <CreditCard className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Plan</p>
                  <p className="text-white font-medium">{plan}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
                <Calendar className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-xs text-green-400">Free Trial Until</p>
                  <p className="text-white font-medium">{formattedDate}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Email Notice */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-400 mt-0.5" />
              <div className="text-left">
                <p className="text-blue-400 font-medium">Check Your Email</p>
                <p className="text-sm text-slate-400">
                  We've sent your temporary password to <span className="text-white">{email}</span>. 
                  Use it to log in and start using your CRM.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={() => navigate('/crm/login')}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold rounded-xl inline-flex items-center gap-2 transition-all hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20"
            data-testid="go-to-login"
          >
            Go to CRM Login
            <ArrowRight className="w-5 h-5" />
          </button>

          {/* Help Text */}
          <p className="mt-6 text-sm text-slate-500">
            Need help? Contact us at{' '}
            <a href="mailto:support@clouditllc.ai" className="text-blue-400 hover:underline">
              support@clouditllc.ai
            </a>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CRMSubscriptionSuccess;
